<script setup lang="ts">
  import type { SupportSession } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'
  import { ref } from 'vue'
  import { Button } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import { useSupportSessionAssignedContext } from '../../composables/useSupportSessionAssigned'

  // Confirm for a Gatship user ending a session from the assigned list.
  // Ending the session the tab is currently inside (with the SignalR
  // teardown and tab-close countdown) is SupportSessionModalEndCurrent,
  // hosted by SupportSessionHeader.

  // Undefined for non-Gatship users — this modal mounts on every surface
  // with a SupportModalProvider, but is only ever shown from the
  // assigned list, which exists solely for Gatship users.
  const supportSessionAssignedService = useSupportSessionAssignedContext()

  const supportSession = ref<SupportSession | undefined>(undefined)

  const showEndSupportSessionModal = ref(false)

  export type ShowEndSupportSessionAssignedDialogParams = {
    supportSession: SupportSession
  }

  function showModal({
    supportSession: session
  }: ShowEndSupportSessionAssignedDialogParams) {
    supportSession.value = session
    showEndSupportSessionModal.value = true
  }

  async function onEndSupportSession() {
    if (supportSession.value?.sessionId && supportSessionAssignedService) {
      await supportSessionAssignedService.commands.endSupportSession(
        supportSession.value.sessionId,
        globalUtils.functions.uuid()
      )
      showEndSupportSessionModal.value = false
    }
  }

  defineExpose({
    showModal
  })
</script>

<template>
  <AlertDialog v-model:open="showEndSupportSessionModal">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>End Support Session</AlertDialogTitle>
      </AlertDialogHeader>
      <div class="flex flex-col gap-4 text-sm leading-none">
        <span>Are you sure you want to end the support session?</span>
        <span>You will no longer be able to access this support session.</span>
      </div>
      <AlertDialogFooter>
        <Button variant="outline" size="lg" @click="onEndSupportSession()">
          End Support Session
        </Button>
        <Button
          variant="ghost"
          size="lg"
          @click="showEndSupportSessionModal = false"
        >
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
